import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Icon,
  IconButton,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  SimpleGrid,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  useToast,
  VStack,
} from "@chakra-ui/react";
import {
  MODAL_CONTENT_SX,
  MODAL_HEADER_SX,
  MODAL_FOOTER_SX,
} from "../../../components/Portal/portalModalChrome";
import { FaCloudUploadAlt, FaTimes } from "react-icons/fa";
import {
  addPoolMember,
  bulkAddPoolMembersFromCvs,
  updatePoolMember,
} from "../../../api/partnersApi";
import { useBusinessUnit } from "../../../context/BusinessUnitContext";
import {
  PARTNERS_POOL_MEMBER_STATUSES,
  PARTNERS_POOL_MEMBER_STATUS_LABELS,
  PARTNERS_POOL_MEMBER_STATUS_LABEL_KEYS,
  getStatusLabel,
} from "../../../constants/statuses";
import { useTranslation } from "react-i18next";

const EMPTY_FORM = {
  first_name: "",
  last_name: "",
  email: "",
  status: "active",
};

const SUPPORTED_EXTS = [".pdf", ".docx"];
const MAX_FILES = 20;

const PoolMemberFormModal = ({ isOpen, onClose, initialData, poolId, onSuccess }) => {
  const { selectedBusinessUnitId } = useBusinessUnit();
  const [form, setForm] = useState(EMPTY_FORM);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [files, setFiles] = useState([]);
  const [bulkResult, setBulkResult] = useState(null);
  const [bulkUploading, setBulkUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const fileInputRef = useRef(null);
  const toast = useToast();
  const { t } = useTranslation("portal");

  const isEdit = Boolean(initialData?.id);

  useEffect(() => {
    if (!isOpen) return;
    if (isEdit) {
      setForm({
        first_name: initialData?.first_name || initialData?.firstName || "",
        last_name: initialData?.last_name || initialData?.lastName || "",
        email: initialData?.email || "",
        status: initialData?.status || "active",
      });
    } else {
      setForm(EMPTY_FORM);
    }
    setFiles([]);
    setBulkResult(null);
    setDragActive(false);
    setActiveTab(0);
  }, [initialData, isEdit, isOpen]);

  const acceptFiles = (incoming) => {
    if (!incoming || !incoming.length) return;
    const valid = incoming.filter((f) => {
      const ext = f.name.includes(".")
        ? `.${f.name.split(".").pop().toLowerCase()}`
        : "";
      return SUPPORTED_EXTS.includes(ext);
    });
    if (valid.length !== incoming.length) {
      toast({
        title: t(
          "partners.pools.members.bulkCv.unsupportedFiltered",
          "Some files were skipped (only PDF/DOCX accepted)"
        ),
        status: "warning",
      });
    }
    setFiles((prev) => {
      const combined = [...prev, ...valid];
      if (combined.length > MAX_FILES) {
        toast({
          title: t(
            "partners.pools.members.bulkCv.tooManyFiles",
            "Maximum 20 files at a time"
          ),
          status: "warning",
        });
      }
      return combined.slice(0, MAX_FILES);
    });
    setBulkResult(null);
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") setDragActive(true);
    else if (e.type === "dragleave") setDragActive(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      acceptFiles(Array.from(e.dataTransfer.files));
    }
  };

  const removeFileAt = (idx) => {
    setFiles((prev) => prev.filter((_, i) => i !== idx));
  };

  const isValid = useMemo(() => {
    return (
      form.first_name.trim().length > 0 &&
      form.last_name.trim().length > 0 &&
      form.email.trim().length > 0
    );
  }, [form.first_name, form.last_name, form.email]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleFilesChange = (e) => {
    acceptFiles(Array.from(e.target.files || []));
    e.target.value = null;
  };

  const handleSubmit = async () => {
    if (!isValid) return;
    setIsSubmitting(true);
    try {
      const payload = {
        firstName: form.first_name.trim(),
        lastName: form.last_name.trim(),
        email: form.email.trim(),
        status: form.status,
      };

      if (isEdit) {
        const updated = await updatePoolMember(poolId, initialData.id, payload, {
          buId: selectedBusinessUnitId || undefined,
        });
        toast({
          title: t("partners.pools.members.toasts.updated", "Member updated"),
          status: "success",
        });
        onSuccess?.(updated);
      } else {
        const created = await addPoolMember(poolId, [payload], {
          buId: selectedBusinessUnitId || undefined,
        });
        toast({
          title: t("partners.pools.members.toasts.created", "Member added"),
          status: "success",
        });
        onSuccess?.(created);
      }
      onClose?.();
    } catch (e) {
      toast({
        title: t(
          "partners.pools.members.toasts.operationFailedTitle",
          "Operation failed"
        ),
        description:
          e?.response?.data?.message ||
          t(
            "partners.pools.members.toasts.operationFailedDescription",
            "An error occurred. Please try again."
          ),
        status: "error",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBulkUpload = async () => {
    if (!files.length) return;
    setBulkUploading(true);
    try {
      const result = await bulkAddPoolMembersFromCvs(poolId, files, {
        buId: selectedBusinessUnitId || undefined,
      });
      setBulkResult(result);
      setFiles([]);
      if ((result?.successCount || 0) > 0) {
        // Refresh parent list — modal stays open showing the results breakdown
        onSuccess?.(result);
      }
    } catch (e) {
      toast({
        title: t(
          "partners.pools.members.bulkCv.toasts.uploadFailed",
          "Bulk upload failed"
        ),
        description: e?.response?.data?.detail || e?.message,
        status: "error",
      });
    } finally {
      setBulkUploading(false);
    }
  };

  const handleBulkClose = () => {
    setBulkResult(null);
    setFiles([]);
    setBulkUploading(false);
    setDragActive(false);
    setActiveTab(0);
    onClose?.();
  };

  const renderManualForm = () => (
    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
      <FormControl isRequired>
        <FormLabel>
          {t(
            "partners.pools.members.form.fields.firstName",
            "First Name"
          )}
        </FormLabel>
        <Input
          name="first_name"
          value={form.first_name}
          onChange={handleChange}
          placeholder={t(
            "partners.pools.members.form.placeholders.firstName",
            "First name"
          )}
        />
      </FormControl>
      <FormControl isRequired>
        <FormLabel>
          {t(
            "partners.pools.members.form.fields.lastName",
            "Last Name"
          )}
        </FormLabel>
        <Input
          name="last_name"
          value={form.last_name}
          onChange={handleChange}
          placeholder={t(
            "partners.pools.members.form.placeholders.lastName",
            "Last name"
          )}
        />
      </FormControl>
      <FormControl isRequired>
        <FormLabel>
          {t("partners.pools.members.form.fields.email", "Email")}
        </FormLabel>
        <Input
          name="email"
          type="email"
          value={form.email}
          onChange={handleChange}
          placeholder={t(
            "partners.pools.members.form.placeholders.email",
            "email@example.com"
          )}
        />
      </FormControl>
      <FormControl>
        <FormLabel>
          {t("partners.pools.members.form.fields.status", "Status")}
        </FormLabel>
        <Select
          name="status"
          value={form.status}
          onChange={handleChange}
          sx={{
            paddingTop: "0 !important",
            paddingBottom: "0 !important",
            lineHeight: "32px !important",
            height: "32px !important",
          }}
        >
          {PARTNERS_POOL_MEMBER_STATUSES.map((s) => (
            <option key={s.value} value={s.value}>
              {getStatusLabel(
                t,
                PARTNERS_POOL_MEMBER_STATUS_LABEL_KEYS,
                PARTNERS_POOL_MEMBER_STATUS_LABELS,
                s.value
              )}
            </option>
          ))}
        </Select>
      </FormControl>
    </SimpleGrid>
  );

  const renderBulkUploadDropzone = () => (
    <VStack align="stretch" spacing={4}>
      <Alert status="info" variant="left-accent">
        <AlertIcon />
        <Text fontSize="sm">
          {t(
            "partners.pools.members.bulkCv.notice",
            "Upload up to 20 PDF/DOCX CVs. Name and email will be extracted automatically."
          )}
        </Text>
      </Alert>
      <FormControl>
        <FormLabel>
          {t(
            "partners.pools.members.bulkCv.fileLabel",
            "CV files (PDF or DOCX)"
          )}
        </FormLabel>
        <Box
          border="2px dashed"
          borderColor={dragActive ? "blue.400" : "gray.300"}
          borderRadius="md"
          p={6}
          minH="140px"
          display="flex"
          alignItems="center"
          justifyContent="center"
          bg={dragActive ? "blue.50" : "gray.50"}
          transition="all 0.2s"
          cursor="pointer"
          onClick={() => fileInputRef.current?.click()}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          _hover={{ bg: "gray.100" }}
        >
          <VStack spacing={2}>
            <Icon as={FaCloudUploadAlt} boxSize={8} color="gray.400" />
            <Text
              fontSize="sm"
              color="gray.600"
              sx={{ marginBottom: "0 !important" }}
            >
              {t(
                "partners.pools.members.bulkCv.dropzone",
                "Drag & drop CV files here, or click to browse"
              )}
            </Text>
            <Text
              fontSize="xs"
              color="gray.400"
              sx={{ marginBottom: "0 !important" }}
            >
              {t(
                "partners.pools.members.bulkCv.accepted",
                "PDF, DOCX — max 20 files"
              )}
            </Text>
          </VStack>
        </Box>
        <input
          type="file"
          ref={fileInputRef}
          style={{ display: "none" }}
          accept=".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
          multiple
          onChange={handleFilesChange}
        />
        {files.length > 0 && (
          <VStack align="stretch" spacing={1} mt={3}>
            <Text fontSize="xs" color="gray.600">
              {t(
                "partners.pools.members.bulkCv.selectedCount",
                { count: files.length, defaultValue: "{{count}} file(s) selected" }
              )}
            </Text>
            {files.map((f, idx) => (
              <Flex
                key={`${f.name}-${idx}`}
                align="center"
                justify="space-between"
                bg="gray.50"
                px={3}
                py={1}
                borderRadius="md"
              >
                <Text fontSize="sm" isTruncated maxW="80%">
                  {f.name}
                </Text>
                <IconButton
                  aria-label="remove"
                  icon={<FaTimes />}
                  size="xs"
                  variant="ghost"
                  onClick={() => removeFileAt(idx)}
                />
              </Flex>
            ))}
          </VStack>
        )}
      </FormControl>
    </VStack>
  );

  const renderBulkUploadResult = () => (
    <VStack align="stretch" spacing={4}>
      {bulkResult.successCount > 0 && (
        <Box>
          <Flex align="center" gap={2} mb={2}>
            <Badge colorScheme="green" fontSize="sm" px={2} py={0.5}>
              {bulkResult.successCount}
            </Badge>
            <Text
              fontWeight="semibold"
              fontSize="sm"
              sx={{ marginBottom: "0 !important" }}
            >
              {t("partners.pools.members.bulkCv.createdTitle", {
                count: bulkResult.successCount,
                defaultValue: "member(s) added successfully",
              })}
            </Text>
          </Flex>
          <VStack align="stretch" spacing={1}>
            {(bulkResult.items || []).map((m) => (
              <Flex
                key={m.id}
                align="center"
                bg="green.50"
                px={3}
                py={1.5}
                borderRadius="md"
              >
                <Text
                  fontSize="sm"
                  fontWeight="medium"
                  sx={{ marginBottom: "0 !important" }}
                >
                  {[m.firstName, m.lastName].filter(Boolean).join(" ") || m.email}
                </Text>
                {m.email && (m.firstName || m.lastName) && (
                  <Text
                    fontSize="xs"
                    color="gray.500"
                    ml={2}
                    sx={{ marginBottom: "0 !important" }}
                  >
                    — {m.email}
                  </Text>
                )}
              </Flex>
            ))}
          </VStack>
        </Box>
      )}
      {bulkResult.errorCount > 0 && (
        <Box>
          <Flex align="center" gap={2} mb={2}>
            <Badge colorScheme="red" fontSize="sm" px={2} py={0.5}>
              {bulkResult.errorCount}
            </Badge>
            <Text
              fontWeight="semibold"
              fontSize="sm"
              sx={{ marginBottom: "0 !important" }}
            >
              {t("partners.pools.members.bulkCv.errorsTitle", {
                count: bulkResult.errorCount,
                defaultValue: "file(s) could not be processed",
              })}
            </Text>
          </Flex>
          <VStack align="stretch" spacing={1}>
            {(bulkResult.errors || []).map((err, idx) => (
              <Box
                key={`err-${idx}`}
                bg={err.error === "DUPLICATE_EMAIL" ? "orange.50" : "red.50"}
                px={3}
                py={2}
                borderRadius="md"
              >
                <Text
                  fontSize="sm"
                  fontWeight="medium"
                  sx={{ marginBottom: "0 !important" }}
                >
                  {err.filename || "?"}
                </Text>
                <Text
                  fontSize="xs"
                  color={err.error === "DUPLICATE_EMAIL" ? "orange.700" : "red.600"}
                  mt={0.5}
                  sx={{ marginBottom: "0 !important" }}
                >
                  {err.error === "DUPLICATE_EMAIL"
                    ? t(
                        "partners.pools.members.bulkCv.errors.duplicateEmail",
                        { email: err.email, defaultValue: "Email \"{{email}}\" is already in this pool" }
                      )
                    : err.error === "EMAIL_NOT_FOUND_IN_CV"
                    ? t(
                        "partners.pools.members.bulkCv.errors.emailNotFound",
                        "No email could be extracted from this CV"
                      )
                    : err.error === "UNSUPPORTED_FILE_TYPE"
                    ? t(
                        "partners.pools.members.bulkCv.errors.unsupportedType",
                        "Unsupported file type (only PDF/DOCX)"
                      )
                    : err.error === "CV_PARSING_FAILED"
                    ? t(
                        "partners.pools.members.bulkCv.errors.parseFailed",
                        "Could not read text from this file"
                      )
                    : err.error === "CV_UPLOAD_FAILED"
                    ? t(
                        "partners.pools.members.bulkCv.errors.uploadFailed",
                        "Storage upload failed"
                      )
                    : err.error || t("common.unknownError", "An error occurred")}
                </Text>
              </Box>
            ))}
          </VStack>
        </Box>
      )}
    </VStack>
  );

  const renderBulkUpload = () =>
    bulkResult ? renderBulkUploadResult() : renderBulkUploadDropzone();

  const isOnBulkTab = !isEdit && activeTab === 1;
  const showResultFooter = isOnBulkTab && bulkResult;
  const showBulkUploadFooter = isOnBulkTab && !bulkResult;

  return (
    <Modal isOpen={isOpen} onClose={handleBulkClose} size="xl">
      <ModalOverlay bg="rgba(11,31,58,.45)" />
      <ModalContent sx={MODAL_CONTENT_SX}>
        <ModalHeader sx={MODAL_HEADER_SX}>
          {isEdit
            ? t("partners.pools.members.form.modalTitleEdit", "Edit Member")
            : t("partners.pools.members.form.modalTitleCreate", "Add Member")}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {isEdit ? (
            renderManualForm()
          ) : (
            <Tabs
              variant="enclosed"
              colorScheme="blue"
              index={activeTab}
              onChange={(idx) => {
                setActiveTab(idx);
                // Reset bulk-upload state when switching back to manual
                if (idx === 0) setBulkResult(null);
              }}
            >
              <TabList>
                <Tab>
                  {t(
                    "partners.pools.members.form.tabs.manual",
                    "Add manually"
                  )}
                </Tab>
                <Tab>
                  {t(
                    "partners.pools.members.form.tabs.bulkCv",
                    "Upload CVs"
                  )}
                </Tab>
              </TabList>
              <TabPanels>
                <TabPanel px={0}>{renderManualForm()}</TabPanel>
                <TabPanel px={0}>{renderBulkUpload()}</TabPanel>
              </TabPanels>
            </Tabs>
          )}
        </ModalBody>
        <ModalFooter sx={MODAL_FOOTER_SX}>
          {showResultFooter ? (
            <Button variant="primary" size="sm" onClick={handleBulkClose}>
              {t("common.close", "Close")}
            </Button>
          ) : (
            <>
              <Flex flex="1" />
              <Button variant="secondary" size="sm" mr={3} onClick={handleBulkClose}>
                {t("partners.pools.members.form.buttons.cancel", "Cancel")}
              </Button>
              {!isOnBulkTab && (
                <Button
                  variant="primary"
                  size="sm"
                  onClick={handleSubmit}
                  isDisabled={!isValid}
                  isLoading={isSubmitting}
                >
                  {isEdit
                    ? t(
                        "partners.pools.members.form.buttons.saveChanges",
                        "Save Changes"
                      )
                    : t("partners.pools.members.form.buttons.add", "Add Member")}
                </Button>
              )}
              {showBulkUploadFooter && (
                <Button
                  variant="primary"
                  size="sm"
                  onClick={handleBulkUpload}
                  isLoading={bulkUploading}
                  isDisabled={files.length === 0}
                >
                  {t(
                    "partners.pools.members.bulkCv.uploadButton",
                    { count: files.length, defaultValue: "Upload {{count}} CV(s)" }
                  )}
                </Button>
              )}
            </>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default PoolMemberFormModal;
